/**
 * Bandeau de consentement aux traceurs (DECISIONS §11, A-41).
 *
 * Trois règles tenues ici :
 * 1. **Rien avant le choix** : le bandeau ne dépose aucun identifiant ; il lit le choix mémorisé et
 *    applique la mesure d’audience seulement si elle a été acceptée (`cookies/mesure-audience.ts`) ;
 * 2. **Aucun retour** : dès qu’un choix existe (et qu’il n’a pas expiré), la bannière disparaît et
 *    ne revient pas au chargement suivant ;
 * 3. **Aucun blocage** : ce n’est pas une fenêtre modale, la page reste utilisable derrière, et le
 *    refus ne retire aucune fonction du jeu.
 *
 * Le bandeau n’est pas affiché sur `/cookies` : cette page porte le même panneau, en permanence.
 * Deux surfaces identiques empilées seraient une gêne, pas une preuve.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
  enregistrerChoixConsentement,
  useChoixConsentement,
  type OrigineChoix,
} from '../cookies/consentement';
import { appliquerChoixMesure } from '../cookies/mesure-audience';
import { Bouton } from './Bouton';
import { PanneauTraceurs } from './PanneauTraceurs';

/** Route qui porte déjà la gestion du consentement. */
const ROUTE_GESTION = '/cookies';

export function BandeauCookies() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const choix = useChoixConsentement();
  const [personnalisation, setPersonnalisation] = useState(false);
  const [mesure, setMesure] = useState(choix?.mesureAudience ?? false);

  /**
   * Applique le choix mémorisé à chaque changement : accepter active la mesure, refuser la
   * supprime. Sans choix connu, rien n’est créé.
   */
  useEffect(() => {
    appliquerChoixMesure(choix?.mesureAudience ?? false);
  }, [choix]);

  /** Enregistre un choix puis referme le panneau de personnalisation. */
  function choisir(valeur: boolean, origine: OrigineChoix): void {
    enregistrerChoixConsentement(valeur, origine);
    setPersonnalisation(false);
  }

  if (choix !== null || pathname === ROUTE_GESTION) return null;

  return (
    <section
      className="bandeau-cookies"
      role="dialog"
      aria-labelledby="bandeau-cookies-titre"
      aria-describedby="bandeau-cookies-texte"
      data-bandeau-cookies="oui"
    >
      <div className="bandeau-cookies__texte">
        <h2 className="bandeau-cookies__titre" id="bandeau-cookies-titre">
          {t('cookies.bandeau.titre')}
        </h2>
        <p id="bandeau-cookies-texte">{t('cookies.bandeau.texte')}</p>
        <p className="bandeau-cookies__lien">
          <Link to={ROUTE_GESTION}>{t('cookies.bandeau.politique')}</Link>
        </p>
      </div>
      <div className="bandeau-cookies__actions">
        <Bouton variante="principal" onClick={() => choisir(true, 'tout-accepter')}>
          {t('cookies.bandeau.toutAccepter')}
        </Bouton>
        <Bouton variante="secondaire" onClick={() => choisir(false, 'refuser')}>
          {t('cookies.bandeau.refuser')}
        </Bouton>
        <Bouton
          variante="discret"
          aria-expanded={personnalisation}
          onClick={() => {
            setPersonnalisation((ouvert) => !ouvert);
          }}
        >
          {t('cookies.bandeau.personnaliser')}
        </Bouton>
      </div>
      {personnalisation ? (
        <PanneauTraceurs
          mesure={mesure}
          onChangerMesure={setMesure}
          onEnregistrer={() => {
            choisir(mesure, 'personnaliser');
          }}
          onAnnuler={() => {
            setPersonnalisation(false);
          }}
        />
      ) : null}
    </section>
  );
}
